// © Microsoft Corporation. All rights reserved.

import { DesignSystem, accentFillRest, highContrastSelector, isDarkMode, neutralFocus, neutralForegroundHint, neutralForegroundRest, neutralLayerCard } from "@microsoft/fast-components-styles-msft";
import { avatarSize, dangerColor, dangerFocusColor, darkerBgColor, fontFamily, primaryDarkerColor, reset, secondaryBorderColor } from "./commonJssConstants";

import { applyLocalizedProperty } from "@microsoft/fast-jss-utilities";

const switchSize = "20px";

export const CommonStyles = (config: DesignSystem) => ({
    mainContainer: {
        ...reset,
        position: "relative",
        "line-height": "initial",
        "font-size": "16px",
        "font-family": fontFamily,
        // 'Hide scrollbar'
        "-ms-overflow-style": "none",
        "&::-webkit-scrollbar": {
            display: "none"
        },

        "& textarea": {
            "font-family": fontFamily
        },

        "& *": {
            "-webkit-font-smoothing": "auto",
            "-moz-osx-font-smoothing": "grayscale",
            "font-smoothing": "auto"
        },
        "& label": {
            "vertical-align": "middle"
        },
        "& .no-select, &.no-select ": {
            "-webkit-touch-callout": "none",
            /* 'iOS Safari '*/
            "-webkit-user-select": "none",
            /* 'Safari '*/
            "-khtml-user-select": "none",
            /* 'Konqueror HTML '*/
            "-moz-user-select": "none",
            /* 'Old versions of Firefox '*/
            "-ms-user-select": "none",
            /* 'Internet Explorer'/'Edge '*/
            "user-select": "none"
            /* 'Non-prefixed version, currently'
            'supported by Chrome, Opera and Firefox '*/
        },
        "& .cwt-hide": {
            display: "none"
        },
        "& .cwt-hide-important": {
            display: "none !important"
        },
        "& .cwt-text-muted": {
            color: neutralForegroundHint
        },
        "& .text-center": {
            "text-align": "center"
        },
        "& .dot-before": {
            "&:before": {
                content: "'•'",
                margin: "0 8px",
                "font-size": "14px",
                "font-weight": "400",
                "line-height": "inherit"
            }
        },
        "& .cwt-capitalize": {
            "text-transform": "capitalize"
        },

        /* 'Comment Highlighting '*/
        "& @keyframes comment-highlight-animation": {
            from: {
                "margin-top": "-10px",
                opacity: "0.1"
            },
            to: {
                "margin-top": "0",
                opacity: "1"
            }
        },
        "& .cwt-comment-highlight": {
            "animation-name": "comment-highlight-animation",
            "animation-duration": ".3s",
            "animation-timing-function": "ease"
        },

        /* 'STANDALONE '*/
        "& a, a:not([href])": {
            color: isDarkMode(config) ? "white" : "#717272",
            cursor: "pointer",

            "&:active": {
                color: darkerBgColor
            }
        },
        "& .cwt-link": {
            cursor: "pointer"
        },
        "& a:not(:hover)": {
            "text-decoration": "none"
        },
        "& a:hover, .cwt-link:hover:not(.cwt-button), .cwt-link.cwt-icon:hover": {
            [`&:not(${highContrastSelector})`]: {
                "text-decoration": "none"
            }
        },
        "& .cwt-select ": {
            padding: "4px",
            color: neutralForegroundHint,
            "border-radius": "4px"
        },

        /* 'BADGE '*/
        "& .cwt-badge": {
            padding: "0 8px 0.8px",
            "font-size": "11px",
            "font-weight": "500",
            background: "rgba(0, 0, 0, 0.07)",
            "border-radius": "16px"
        },

        /* 'ICONS '*/
        "& .cwt-icon": {
            display: "flex",
            "align-items": "center",
            "font-style": "normal",
            color: neutralForegroundRest,

            "& .cwt-icon-label": {
                padding: "0 5px",
                "font-style": "inherit",
                color: neutralForegroundRest,
                "margin-top": "-.12em"
            },

            "&[disabled] ": {
                "pointer-events": "none",
                cursor: "default"
            },

            "&.cwt-link:hover": {
                "text-decoration": "none"
            }
        },
        "& .cwt-icon, * & .cwt-icon": {
            color: neutralForegroundRest,

            "& .cwt-icon-vector": {
                "font-size": "inherit"
            }
        },

        "& .cwt-fast-icon-button": {
            "&, &:active, &:hover": {
                background: "none",
                border: "none"
            }
        },

        // 'Vector Icons'
        "& .cwt-icon-vector": {
            width: "1em",
            fill: "inherit"
        },
        "& a:hover": {
            "& .cwt-icon": {
                color: accentFillRest
            }
        },
        /* 'SWITCH '*/
        "& .cwt-switch": {
            position: "relative",
            display: "inline-block",
            width: `calc(${switchSize} * 2)`,
            height: `calc(${switchSize})`,

            "& input": {
                opacity: "0",
                width: "0",
                height: "0",

                "&:checked + .cwt-switch-slider": {
                    "background-color": "#106EBE",
                    border: "1px solid #106EBE",

                    "&:before": {
                        "-webkit-transform": applyLocalizedProperty(`translateX(${switchSize})`, `translateX(-${switchSize})`, config.direction),
                        "-ms-transform": applyLocalizedProperty(`translateX(${switchSize})`, `translateX(-${switchSize})`, config.direction),
                        transform: applyLocalizedProperty(`translateX(${switchSize})`, `translateX(-${switchSize})`, config.direction),
                        "background-color": "white"
                    }
                },

                "&:disabled + .cwt-switch-slider": {
                    opacity: ".5"
                }
            }
        },
        "& .cwt-switch-slider": {
            position: "absolute",
            cursor: "pointer",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            "background-color": "white",
            border: "1px solid #323130",
            "-webkit-transition": ".3s",
            transition: ".3s",
            "border-radius": switchSize,

            "&:before": {
                position: "absolute",
                content: "' '",
                height: `calc(${switchSize} - 8px)`,
                width: `calc(${switchSize} - 8px)`,
                [applyLocalizedProperty("left", "right", config.direction)]: "calc(4px - 1px)",
                bottom: "calc(4px - 1px)",
                "background-color": "#323130",
                "-webkit-transition": ".4s",
                transition: ".4s",
                "border-radius": "50%"
            }
        },

        /* 'TABS '/ 'TABBAR '*/
        "& .cwt-tabs": {
            margin: "0 -4px",
            padding: "0",
            display: "flex",
            "align-items": "center",
            "list-style": "none",
            "overflow-x": "auto",
            "overflow-y": "hidden",

            // 'Hide scrollbar'
            "-ms-overflow-style": "none",
            "&::-webkit-scrollbar ": {
                display: "none"
            },
            "&:-moz-any(#content,#appcontent) browser": {
                [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "-14px !important",
                "overflow-y": "scroll",
                "margin-bottom": "-14px !important",
                "overflow-x": "scroll"
            },

            "& .cwt-tab-item": {
                padding: "4px",
                cursor: "pointer",

                "&:hover": {
                    "background-color": "rgba(0, 0, 0, .05)"
                }
            }
        },

        /* 'BUTTON '*/

        "& .cwt-danger": {
            background: dangerColor,

            "&:hover": {
                background: dangerFocusColor,
                color: neutralLayerCard
            }
        },
        /* 'AVATAR '*/
        "& .cwt-avatar": {
            cursor: "pointer",
            "min-width": avatarSize,
            "max-width": avatarSize,
            height: avatarSize,

            "box-sizing": "border-box",
            "object-fit": "cover",
            "border-radius": "50%"
        },

        "& input": {
            font: "unset"
        },

        // ACCESSIBILITY HELPERS
        "& .cwt-visible-on-focus": {
            display: "inline-flex !important",
            width: "0",
            height: "0"
        },
        "& .cwt-visible-on-focus:focus": {
            width: "1em",
            background: "transparent",
            color: neutralForegroundRest,
            fill: neutralForegroundRest,
            border: "none",
            height: "1em",
            "font-size": "16px",
            opacity: "1.0",
            margin: "0 3px",
            "&.mr-12": {
                [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: "12px"
            }
        }

    }
});